import { useState, useCallback, useEffect } from 'react';
import type { ProcedureType } from '../../../../../../services/ConfigurationService/types';
import type { ScheduledProcedureCodeType } from '../../../../../../services/EncounterService/types/encounterTypes';

export interface ScheduledProcedureCodeEditFormData {
  code: string;
  description: string;
}

const createInitialFormData = (scheduledProcedureCode: ScheduledProcedureCodeType | null): ScheduledProcedureCodeEditFormData => {
  if (!scheduledProcedureCode) {
    return {
      code: '',
      description: '',
    };
  }
  
  return {
    code: scheduledProcedureCode.code || '',
    description: scheduledProcedureCode.description || '',
  };
};

export const useScheduledProcedureCodeEditForm = (
  scheduledProcedureCode: ScheduledProcedureCodeType | null,
  open: boolean,
  procedures: ProcedureType[]
) => {
  const [formData, setFormData] = useState<ScheduledProcedureCodeEditFormData>(createInitialFormData(scheduledProcedureCode));
  const [selectedProcedure, setSelectedProcedure] = useState<ProcedureType | null>(null);
  const [error, setError] = useState('');

  // Update form data when scheduledProcedureCode prop changes
  useEffect(() => {
    if (open && scheduledProcedureCode) {
      setFormData(createInitialFormData(scheduledProcedureCode));
      setError('');
      
      // Find the procedure that matches the current code
      if (procedures && scheduledProcedureCode.code) {
        const procedure = procedures.find(p => p.code === scheduledProcedureCode.code);
        setSelectedProcedure(procedure || null);
      }
    }
  }, [open, scheduledProcedureCode, procedures]);

  const handleInputChange = useCallback((field: keyof ScheduledProcedureCodeEditFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  }, [error]);

  const handleProcedureChange = useCallback((procedure: ProcedureType | null) => {
    setSelectedProcedure(procedure);
    if (procedure) {
      setFormData(prev => ({
        ...prev,
        code: procedure.code || '',
        description: procedure.description || ''
      }));
    }
    // Clear error when user makes a selection
    if (error) setError('');
  }, [error]);

  const validateForm = useCallback((): boolean => {
    if (!selectedProcedure) {
      setError('Please select a procedure from the dropdown');
      return false;
    }
    return true;
  }, [selectedProcedure]);

  const resetForm = useCallback(() => {
    setFormData(createInitialFormData(null));
    setSelectedProcedure(null);
    setError('');
  }, []);

  return {
    formData,
    selectedProcedure,
    error,
    setError,
    handleInputChange,
    handleProcedureChange,
    validateForm,
    resetForm,
  };
};
